<template lang="pug">
div(
    :class="{ active: modelState, darkmode: dark }"
    class="tl-dialog"
)
    transition(
        :name="transition"
    )
        tl-position(
            v-if="modelState"
            ref="dialog"
            class="fill-height full-width tl-dialog-content"
            x="center"
            y="center"
            @click="(e) => clickOutside(e)"
        )
            slot
</template>

<script setup>
import { ref, computed, watch, inject } from 'vue'
import { promiseTimeout } from '@vueuse/core'
import { getInjects } from '@/components/$tl/utils/injects'

const props = defineProps({
    modelValue: Boolean,
    persistant: Boolean,
    transitionReversed: Boolean,
    dark: Boolean,
})
const emit = defineEmits(['update:modelValue'])
const injects = getInjects(props, inject)

const dialog = ref(null)
const modelState = ref(false)
const modelValue = computed(() => props.modelValue)

const persistant = computed(() => props.persistant || injects.persistant)
const dark = computed(() => props.dark || injects.dark)
const transition = computed(() => (props.transitionReversed || injects.transitionReversed ? 'dialog-content-reversed' : 'dialog-content'))

modelState.value = modelValue.value

watch(modelValue, (val) => {
    if (val !== modelState.value) modelState.value = val
})

watch(modelState, (val) => {
    emit('update:modelValue', val)

    const body = document.querySelector('body')
    if (val) {
        body.style.overflow = 'hidden'
    } else {
        body.style.overflow = 'auto'
    }
})

const clickOutside = async (event) => {
    if (event.target.classList.contains('tl-dialog-content') && !persistant.value) {
        modelState.value = false
    } else if (persistant.value) {
        dialog.value.$el.classList.add('shake')
        await promiseTimeout(500)
        dialog.value.$el.classList.remove('shake')
    }
}
</script>

<style lang="sass" scoped>

.tl-dialog
    position: fixed
    top: 0
    left: 0
    width: 100vw
    height: 100vh
    background-color: rgba(0,0,0,0.0)
    pointer-events: none
    z-index: 1000
    transition: 0.5s ease-in-out
    backdrop-filter: saturate(100%) blur(0px)
    &.active
        background-color: rgba(0,0,0,0.65)
        pointer-events: all
        backdrop-filter: saturate(180%) blur(5px)
    .tl-dialog-content
        perspective: 1000px

.shake
    animation: shake 0.52s cubic-bezier(.36,.07,.19,.97) both .32s

@keyframes shake
    10%, 90%
        transform: translate3d(-1px, 0, 0)

    20%, 80%
        transform: translate3d(2px, 0, 0)

    30%, 50%, 70%
        transform: translate3d(-4px, 0, 0)

    40%, 60%
        transform: translate3d(4px, 0, 0)

.dialog-content-enter-active
    transition: all .35s ease-in-out

.dialog-content-enter-from
    opacity: 0
    transform: translateY(70vh) scale(0.9)

.dialog-content-enter-to
    opacity: 1

.dialog-content-leave-active
    transition: all .35s ease-in-out

.dialog-content-leave-from
    opacity: 1

.dialog-content-leave-to
    opacity: 0
    transform: translateY(70vh) scale(0.9)

.dialog-content-reversed-enter-active
    transition: all .35s ease-in-out

.dialog-content-reversed-enter-from
    opacity: 0
    transform: translateY(-70vh) scale(0.9)

.dialog-content-reversed-enter-to
    opacity: 1

.dialog-content-reversed-leave-active
    transition: all .35s ease-in-out

.dialog-content-reversed-leave-from
    opacity: 1

.dialog-content-reversed-leave-to
    opacity: 0
    transform: translateY(-70vh) scale(0.9)
</style>
